import React, { useState } from 'react';
import { Form as FinalForm } from 'react-final-form';
import { useSelector, useDispatch } from 'react-redux';
import isEqual from 'lodash/isEqual';

import { FormattedMessage } from '../../util/reactIntl';
import FieldDateAndTimeInput from '../OrderPanel/BookingTimeForm/FieldDateAndTimeInput';
import { fetchCartListingTimeSlots } from '../../ducks/cart.duck';
import { dayCountAvailableForBooking } from '../../config/configStripe';
import { stringifyDateToISO8601 } from '../../util/dates';
import css from './CartDrawerDateTimePicker.module.css';

/**
 * RAS-2512: inline date+time picker for a cart-item group's hourly-tier items.
 * Reuses FieldDateAndTimeInput (the same component BookingTimeForm.js uses on the
 * listing page's hourly booking panel) rather than a parallel implementation - its
 * own available-start/end-time computation already derives correctly from fetched
 * time slots, including same-day/past-time exclusion (the same logic the live
 * booking flow depends on), so nothing extra is layered on top of it here.
 *
 * NOTE: FieldDateAndTimeInput is inherently single-listing (it takes one
 * `listingId`/`monthlyTimeSlots`) - the exact TIME-OF-DAY availability shown always
 * comes from just the group's first/primary listing, not a full union. RAS-2506
 * follow-up (Gap A, partial fix): `extraBlockedDates` adds a DAY-LEVEL guard on top -
 * CartDatesEditor.js fetches the same RAS-2509 group-availability data the
 * day-range picker uses (a day blocked for ANY listing in the group) and passes it
 * through here, so a day can't even be selected if it's blocked for another hourly
 * listing sharing this date-edit group - even though the exact hour/minute slots
 * available within an allowed day still reflect only the primary listing. Full
 * time-slot-level union across listings would need FieldDateAndTimeInput itself
 * rebuilt to merge multi-listing slot data, which it isn't built for - flagged as the
 * remaining, smaller known gap, not a silent shortcut.
 *
 * RAS-2506 follow-up: the start-date calendar renders `inline` (react-dates'
 * DayPickerSingleDateController, always visible - no text-input/popup) with its own
 * Remove/Apply footer, matching the day-range picker's calendar-embedded Clear/Apply
 * exactly, instead of a separate Clear/Apply row below the whole date+time-selects
 * block. The real "save to cart" action lives in the onApply/onRemove passed here,
 * same as CartDrawerDateRangePicker.js's handleRealApply - inline mode has no
 * focus/blur concept (unlike the popup-style SingleDatePicker DateInput.js normally
 * renders), so there's no risk of this auto-firing just from clicking into the
 * Start/End time selects below.
 */
const CartDrawerDateTimePicker = ({
  listing,
  selectedPackage,
  initialStartDate,
  initialStartTime,
  initialEndDate,
  initialEndTime,
  minimumMinutes,
  timeZone,
  intl,
  onApply,
  onClear,
  extraBlockedDates,
}) => {
  const dispatch = useDispatch();
  const [applyInProgress, setApplyInProgress] = useState(false);
  const [applyError, setApplyError] = useState(null);

  const listingId = listing?.id;
  const monthlyTimeSlots = useSelector(
    state => state.cart.monthlyTimeSlotsByListing[listingId?.uuid] || {}
  );
  const maxAvailableSeats = listing?.attributes?.publicData?.quantity;

  const onFetchTimeSlots = (fetchListingId, start, end, tz) =>
    dispatch(fetchCartListingTimeSlots(fetchListingId, start, end, tz));

  return (
    <FinalForm
      onSubmit={() => {}}
      initialValues={{
        bookingStartDate: { date: initialStartDate },
        bookingStartTime: initialStartTime ? +initialStartTime : null,
        bookingEndDate: { date: initialEndDate },
        bookingEndTime: initialEndTime ? +initialEndTime : null,
      }}
      // Same reset-mid-edit hazard CartDrawerDateRangePicker.js guards against -
      // CartDatesEditor re-renders (and rebuilds these initialValues as fresh Date/
      // object references) on every progressive time-slot fetch update while this
      // picker is open.
      initialValuesEqual={(a, b) => isEqual(a, b)}
      render={({ values, form }) => {
        const durationMinutes =
          values.bookingStartTime && values.bookingEndTime
            ? (values.bookingEndTime - values.bookingStartTime) / (60 * 1000)
            : null;
        // Defensive re-check mirroring CartDrawerDateRangePicker's handleRealApply -
        // isDayBlocked (via extraBlockedDates, see FieldDateAndTimeInput.js) already
        // keeps the calendar from producing a blocked selection, this just guards
        // against extraBlockedDates having changed (e.g. another listing in the group
        // just became unavailable on this day) since the last render.
        const startDateBlocked =
          !!extraBlockedDates &&
          !!values.bookingStartDate?.date &&
          extraBlockedDates.has(stringifyDateToISO8601(values.bookingStartDate.date, timeZone));
        const valid =
          !!values.bookingStartDate?.date &&
          !!values.bookingEndDate?.date &&
          !!values.bookingStartTime &&
          !!values.bookingEndTime &&
          values.bookingEndTime > values.bookingStartTime &&
          (!minimumMinutes || durationMinutes >= minimumMinutes) &&
          !startDateBlocked;

        const handleRealApply = async () => {
          if (!valid || applyInProgress) return;
          setApplyInProgress(true);
          setApplyError(null);
          try {
            await onApply({
              startDate: new Date(values.bookingStartTime),
              endDate: new Date(values.bookingEndTime),
            });
            // On success the parent (CartDatesEditor) unmounts this component -
            // don't touch state after that.
          } catch (e) {
            setApplyError(e?.message || 'Something went wrong applying the new times. Please try again.');
            setApplyInProgress(false);
          }
        };

        return (
          <div className={css.root}>
            {monthlyTimeSlots && timeZone ? (
              <FieldDateAndTimeInput
                className={css.field}
                startDateInputProps={{
                  label: intl.formatMessage({
                    id: 'CartDrawerDateTimePicker.startDateLabel',
                    defaultMessage: 'Start date',
                  }),
                  placeholderText: 'Select date',
                }}
                endDateInputProps={{
                  label: intl.formatMessage({
                    id: 'CartDrawerDateTimePicker.endDateLabel',
                    defaultMessage: 'End date',
                  }),
                  placeholderText: 'Select date',
                }}
                listingId={listingId}
                onFetchTimeSlots={onFetchTimeSlots}
                monthlyTimeSlots={monthlyTimeSlots}
                values={values}
                intl={intl}
                form={form}
                pristine={false}
                timeZone={timeZone}
                dayCountAvailableForBooking={dayCountAvailableForBooking}
                selectedPackage={selectedPackage}
                maxAvailableSeats={maxAvailableSeats}
                extraBlockedDates={extraBlockedDates}
                inlineStartDateCalendar
                renderControllers
                onApply={handleRealApply}
                applyDisabled={!valid}
                applyInProgress={applyInProgress}
                onRemove={() => {
                  setApplyError(null);
                  onClear?.();
                }}
              />
            ) : null}

            {minimumMinutes > 0 && durationMinutes != null && durationMinutes < minimumMinutes && (
              <p className={css.error}>
                <FormattedMessage
                  id="CartDrawerDateTimePicker.belowMinimumDuration"
                  defaultMessage="This booking must be at least {minutes} minutes."
                  values={{ minutes: minimumMinutes }}
                />
              </p>
            )}

            {applyError && <p className={css.error}>{applyError}</p>}
          </div>
        );
      }}
    />
  );
};

export default CartDrawerDateTimePicker;
